// MMO 공유 필드 렌더 묶음(통합 2026-10-28): 다른 플레이어(peers.ts, 레인 M) + 월드 보스 자리(서버 권위 체력 — 모델은 후속, 지금은 그레이박스).
// 필드 장면에서 view.field.net이 있을 때만 보인다. 영웅 모델 GLB는 처음 연결될 때 한 번 읽는다. 프레임 경로에서 새 객체를 만들지 않는다.
// 시계: 다른 플레이어 보간(samplePeer)은 FieldNet이 스냅 시각을 찍는 시계(performance.now)로 읽어야 한다.
//   렌더 실시간 누적(GameClocks.realMs)은 첫 프레임부터 0으로 세고 큰 프레임을 0.25초로 자르므로 늘 뒤처진다 —
//   그 값을 넘기면 보간 비율이 늘 0으로 잘려 다른 플레이어가 10Hz 스냅마다 한 칸 늦게 뚝뚝 끊겨 움직였다(E2E 2026-10-28).
import * as THREE from 'three/webgpu';
import { BossPhase, FIELD_BOSS, type FieldPeer } from '../../shared/fieldNet';
import { Scene, type GameView } from '../../shared/types';
import { PeersVisual } from './peers';

const NO_PEERS: readonly FieldPeer[] = [];

export class FieldMmoRender {
  readonly group = new THREE.Group();
  readonly peers = new PeersVisual();
  private loading = false;
  private readonly boss = new THREE.Group();
  private readonly owned: { dispose(): void }[] = [];

  /** now: FieldNet과 같은 시계(ms). 시험은 가짜를 넣는다 */
  constructor(private readonly now: () => number = () => performance.now()) {
    this.group.name = 'field-mmo';
    this.group.visible = false;
    this.group.add(this.peers.group);
    const mat = new THREE.MeshToonMaterial({ color: 0x6d8a3c });
    const body = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 3, 5.5, 14), mat);
    body.position.y = 2.75; body.castShadow = true;
    const head = new THREE.Mesh(new THREE.SphereGeometry(1.8, 16, 12), mat);
    head.position.y = 6.4; head.castShadow = true;
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xff2a3d, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthWrite: false });
    const ring = new THREE.Mesh(new THREE.RingGeometry(FIELD_BOSS.range - 0.3, FIELD_BOSS.range, 64), ringMat);
    ring.rotation.x = -Math.PI / 2; ring.position.y = 0.05;
    this.boss.add(body, head, ring);
    this.boss.position.set(FIELD_BOSS.x, 0, FIELD_BOSS.z);
    this.boss.visible = false;
    this.group.add(this.boss);
    this.owned.push(body.geometry, head.geometry, ring.geometry, mat, ringMat);
  }

  /** 다른 플레이어 수(QA) */
  get drawn(): number { return this.peers.drawn; }

  update(view: GameView): void {
    const nowMs = this.now();
    const net = view.flow.scene === Scene.Field ? view.field?.net ?? null : null;
    this.group.visible = net !== null;
    if (!net) return;
    if (!this.loading) { this.loading = true; void this.peers.load(); }
    this.peers.update(net.peers ?? NO_PEERS, nowMs);
    const b = net.boss;
    this.boss.visible = !!b && b.phase === BossPhase.Alive;
    if (this.boss.visible && b) { this.boss.position.set(b.x, 0, b.z); this.boss.rotation.y = nowMs / 4000; }
  }

  dispose(): void {
    this.peers.dispose();
    for (const o of this.owned) o.dispose();
    this.owned.length = 0;
  }
}
